import React, { useEffect, useRef, useState } from "react";

import AddCircle from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Alert,
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    Collapse,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { ClipLoader } from "react-spinners";

import {
    getEvents,
    getSignalSightTaskActions,
} from "helpers/destinations/createDestination";

import StandartButton from "components/atoms/StandartButton";

// Type definitions
interface ActionParameter {
    name: string;
    code: string;
    type: string;
    required: boolean;
}

interface Action {
    parameters: ActionParameter[];
    code: string;
    name: string;
}

interface ParameterValue {
    value: string;
    required: boolean;
    type: string;
    name: string;
}

interface FieldRow {
    event_name: {
        options: Array<{ label: string; value: string }>;
        isLoading: boolean;
        value: string | null;
    };
    action_code: {
        options: Array<{
            label: string;
            value: string;
            parameters: ActionParameter[];
        }>;
        isLoading: boolean;
        value: string | null;
    };
    parameters: {
        value: Record<string, ParameterValue>;
        isLoading: boolean;
    };
    isExpanded?: boolean;
    id?: string;
}

// Validation error interface
interface ValidationErrors {
    name?: string;
    fields?: {
        [rowIndex: number]: {
            event_name?: string;
            action_code?: string;
            parameters?: {
                [paramCode: string]: string;
            };
        };
    };
}

interface SignalSightTaskDestinationProps {
    destinationCode: string;
    onNameChange: (name: string) => void;
    onFieldsChange: (fields: FieldRow[]) => void;
    signalSightTaskStep?: number;
    isModal?: boolean;
    buttonRef: React.RefObject<HTMLButtonElement>;
    setButtonDisabledStatus: (disabled: boolean) => void;
}

const SignalSightTaskDestination = ({
    buttonRef,
    destinationCode,
    setButtonDisabledStatus,
    onNameChange,
    onFieldsChange,
    isModal = false,
}: SignalSightTaskDestinationProps) => {
    const [name, setName] = useState<string>("");
    const [actions, setActions] = useState<Action[]>([]);
    const [events, setEvents] = useState<
        Array<{ label: string; value: string }>
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {},
    );
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const [fields, setFields] = useState<FieldRow[]>([
        {
            event_name: {
                options: [],
                isLoading: false,
                value: null,
            },
            action_code: {
                options: [],
                isLoading: false,
                value: null,
            },
            parameters: {
                value: {},
                isLoading: false,
            },
            isExpanded: true,
            id: `field-${Date.now()}-0`,
        },
    ]);

    // Refs for focus management
    const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    // Generate unique ID for fields
    const generateFieldId = () =>
        `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Focus on a specific field
    const focusField = (fieldId: string, inputName?: string) => {
        setTimeout(() => {
            const fieldElement = fieldRefs.current[fieldId];
            if (fieldElement) {
                if (inputName) {
                    const input = fieldElement.querySelector(
                        `input[name="${inputName}"]`,
                    ) as HTMLInputElement;
                    if (input) {
                        input.focus();
                        input.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                } else {
                    fieldElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }
        }, 100);
    };

    // Fetch available events
    const fetchEvents = async () => {
        try {
            const response = await getEvents();
            if (response.status) {
                const options = response.data?.events?.map((event) => ({
                    label: event?.name,
                    value: event?.code,
                }));
                setEvents(options);
                setFields(
                    fields?.map((field) => {
                        field.event_name.options = options;
                        return field;
                    }),
                );
            }
        } catch (error) {
            setError("Failed to fetch events");
        }
    };

    // Fetch available actions
    const fetchActions = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getSignalSightTaskActions();
            if (response.status) {
                const actionsData = response.data?.actions || [];
                setActions(actionsData);

                // Create options for actions
                const actionOptions = actionsData.map((action) => ({
                    label: action.name,
                    value: action.code,
                    parameters: action.parameters || [],
                }));

                setFields(
                    fields?.map((field) => {
                        field.action_code.options = actionOptions;
                        return field;
                    }),
                );
            } else {
                setError(response.message || "Failed to fetch actions");
            }
        } catch (error) {
            setError("Failed to fetch actions");
        } finally {
            setIsLoading(false);
        }
    };

    // URL validation function
    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Validation function to check if form is valid and return errors
    const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
        const errors: ValidationErrors = {};

        // Check if name is provided
        if (!name || name.trim() === "") {
            errors.name = "Name is required";
        }

        // Check if at least one row exists
        if (fields.length === 0) {
            errors.fields = {
                0: { event_name: "At least one mapping is required" },
            };
            return { isValid: false, errors };
        }

        // Check each row for required fields
        errors.fields = {};
        for (let rowIndex = 0; rowIndex < fields.length; rowIndex++) {
            const row = fields[rowIndex];
            const rowErrors: any = {};

            // Event name is required
            if (!row.event_name.value || row.event_name.value.trim() === "") {
                rowErrors.event_name = "Event is required";
            }

            // Action is required
            if (!row.action_code.value || row.action_code.value.trim() === "") {
                rowErrors.action_code = "Action is required";
            }

            // Check required parameters
            const parameters = row.parameters.value;
            const paramErrors: { [paramCode: string]: string } = {};

            for (const [paramCode, paramData] of Object.entries(parameters)) {
                if (
                    paramData.required &&
                    (!paramData.value || paramData.value.trim() === "")
                ) {
                    paramErrors[paramCode] = `${paramData.name} is required`;
                }

                // URL validation for url type parameters
                if (
                    paramData.type === "url" &&
                    paramData.value &&
                    paramData.value.trim() !== ""
                ) {
                    if (!isValidUrl(paramData.value)) {
                        paramErrors[paramCode] =
                            "Please enter a valid URL (e.g., https://example.com)";
                    }
                }
            }

            if (Object.keys(paramErrors).length > 0) {
                rowErrors.parameters = paramErrors;
            }

            if (Object.keys(rowErrors).length > 0) {
                errors.fields[rowIndex] = rowErrors;
            }
        }
        if (Object.keys(errors.fields).length === 0) {
            delete errors.fields;
        }
        return { isValid: Object.keys(errors).length === 0, errors };
    };

    // Build the empty parameter set for an action; each action type has its
    // own fields, so the row's parameters are rebuilt whenever the action
    // changes (the previous action's values and errors no longer apply).
    const buildParametersForAction = (
        actionCode: string | null,
    ): Record<string, ParameterValue> => {
        const selectedAction = actions.find(
            (action) => action.code === actionCode,
        );
        const initialParams: Record<string, ParameterValue> = {};
        selectedAction?.parameters?.forEach((param) => {
            initialParams[param.code] = {
                value: "",
                required: param.required,
                type: param.type,
                name: param.name,
            };
        });
        return initialParams;
    };

    // Handle field changes
    const handleFieldChange = ({
        rowIndex,
        name,
        value,
    }: {
        rowIndex: number;
        name: string;
        value: any;
    }) => {
        setFields(
            fields?.map((field, index) => {
                if (rowIndex !== index) return field;

                if (name === "action_code") {
                    const actionCode = value || null;
                    const parameters = buildParametersForAction(actionCode);
                    return {
                        ...field,
                        action_code: {
                            ...field.action_code,
                            value: actionCode,
                        },
                        parameters: { ...field.parameters, value: parameters },
                        // Auto-expand when the action has parameters
                        isExpanded: Object.keys(parameters).length > 0,
                    };
                }

                if (name === "parameters") {
                    return {
                        ...field,
                        parameters: { ...field.parameters, value },
                    };
                }

                return { ...field, [name]: { ...field[name], value } };
            }),
        );
    };

    // Handle parameter changes
    const handleParameterChange = (
        rowIndex: number,
        paramCode: string,
        value: string,
    ) => {
        setFields(
            fields?.map((field, index) => {
                if (rowIndex !== index) return field;
                const current = field.parameters.value[paramCode];
                if (!current) return field;
                return {
                    ...field,
                    parameters: {
                        ...field.parameters,
                        value: {
                            ...field.parameters.value,
                            [paramCode]: { ...current, value },
                        },
                    },
                };
            }),
        );
    };

    // Handle name change
    const handleNameChange = (value: string) => {
        setName(value);
        onNameChange(value);
    };

    // Toggle expansion state
    const toggleExpansion = (rowIndex: number) => {
        setFields(
            fields?.map((field, index) => {
                if (rowIndex === index) {
                    field.isExpanded = !field.isExpanded;
                }
                return field;
            }),
        );
    };

    // Check if a URL parameter is valid
    const isUrlParameterValid = (paramData: ParameterValue): boolean => {
        if (
            paramData.type === "url" &&
            paramData.value &&
            paramData.value.trim() !== ""
        ) {
            return isValidUrl(paramData.value);
        }
        return true;
    };

    // Add new row with focus management
    const handleAddNewRow = (): void => {
        const newFieldId = generateFieldId();
        const newFields = [
            ...fields,
            {
                event_name: {
                    options: events,
                    isLoading: false,
                    value: null,
                },
                action_code: {
                    options: actions.map((action) => ({
                        label: action.name,
                        value: action.code,
                        parameters: action.parameters || [],
                    })),
                    isLoading: false,
                    value: null,
                },
                parameters: {
                    value: {},
                    isLoading: false,
                },
                isExpanded: true,
                id: newFieldId,
            },
        ];

        setFields(newFields);

        // Focus on the new field's event input
        setTimeout(() => {
            focusField(newFieldId, "event_name");
        }, 150);
    };

    // Delete row
    const handleDeleteRow = (rowIndex: number): void => {
        setFields(fields?.filter((_, index) => index !== rowIndex));
    };

    useEffect(() => {
        if (destinationCode === "sstask") {
            fetchEvents();
            fetchActions();
        }
    }, [destinationCode]);

    useEffect(() => {
        const currentButtonRef = buttonRef.current;
        if (currentButtonRef) {
            const handleButtonClick = (e) => {
                setHasAttemptedSave(true);
                const { isValid, errors } = validateForm();
                setValidationErrors(errors);

                if (!isValid) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };

            currentButtonRef.addEventListener("click", handleButtonClick);

            return () => {
                if (currentButtonRef) {
                    currentButtonRef.removeEventListener(
                        "click",
                        handleButtonClick,
                    );
                }
            };
        }
    }, [buttonRef, fields, name, hasAttemptedSave]);

    useEffect(() => {
        onFieldsChange(fields);
    }, [fields]);

    // Keep validation errors and the host save button in sync with the form.
    // Runs on every name / row / action / parameter change, so switching the
    // action type re-validates against the new action's parameter set.
    // Errors are only surfaced (and the button only locked) after the first
    // save attempt, matching the click interception above.
    useEffect(() => {
        const { isValid, errors } = validateForm();
        if (hasAttemptedSave) {
            setValidationErrors(errors);
        }
        setButtonDisabledStatus(hasAttemptedSave && !isValid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, name, hasAttemptedSave, setButtonDisabledStatus]);

    // Render parameter inputs for a specific action
    const renderParameters = (
        rowIndex: number,
        parameters: Record<string, ParameterValue>,
    ) => {
        if (!parameters || Object.keys(parameters).length === 0) {
            return (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        No parameters required for this action
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    p: 2,
                    // Mobile responsiveness for parameters section
                    ...(isModal && {
                        "@media (max-width: 600px)": {
                            p: 1,
                        },
                    }),
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "text.primary",
                        // Mobile responsiveness for parameters title
                        ...(isModal && {
                            "@media (max-width: 600px)": {
                                fontSize: "0.9rem",
                                mb: 1,
                            },
                        }),
                    }}
                >
                    Action Parameters
                </Typography>
                <Stack spacing={2}>
                    {Object.entries(parameters).map(
                        ([paramCode, paramData]) => {
                            const hasError =
                                hasAttemptedSave &&
                                validationErrors.fields?.[rowIndex]
                                    ?.parameters?.[paramCode];

                            return (
                                <TextField
                                    key={paramCode}
                                    label={paramData.name}
                                    variant="outlined"
                                    size="small"
                                    required={paramData.required}
                                    type={
                                        paramData.type === "numeric"
                                            ? "number"
                                            : "text"
                                    }
                                    placeholder={
                                        paramData.type === "url"
                                            ? "https://example.com"
                                            : `Enter ${paramData.name.toLowerCase()}`
                                    }
                                    value={paramData.value}
                                    onChange={(e) =>
                                        handleParameterChange(
                                            rowIndex,
                                            paramCode,
                                            e.target.value,
                                        )
                                    }
                                    error={Boolean(
                                        hasError ||
                                        (paramData.type === "url" &&
                                            paramData.value &&
                                            paramData.value.trim() !== "" &&
                                            !isUrlParameterValid(paramData)),
                                    )}
                                    helperText={
                                        hasError ||
                                        (paramData.type === "url" &&
                                        paramData.value &&
                                        paramData.value.trim() !== "" &&
                                        !isUrlParameterValid(paramData)
                                            ? "Please enter a valid URL (e.g., https://example.com)"
                                            : paramData.required
                                              ? "This field is required"
                                              : "") ||
                                        ""
                                    }
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                "& .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            "primary.main",
                                                    },
                                            },
                                            "&.Mui-focused": {
                                                "& .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            "primary.main",
                                                        borderWidth: 2,
                                                    },
                                            },
                                        },
                                    }}
                                />
                            );
                        },
                    )}
                </Stack>
            </Box>
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <Stack spacing={2} alignItems="center">
                    <ClipLoader color="#000" size={40} />
                    <Typography variant="body2" color="text.secondary">
                        Loading configuration...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        destinationCode === "sstask" && (
            <>
                <FormControl fullWidth className="mb-3">
                    <TextField
                        label="Name"
                        required
                        value={name || ""}
                        onChange={(e) => handleNameChange(e.target.value)}
                        inputProps={{
                            "data-cy": "create-destination-sstask-name",
                        }}
                        error={hasAttemptedSave && !!validationErrors.name}
                        helperText={hasAttemptedSave && validationErrors.name}
                    />
                </FormControl>
                <Box
                    sx={{
                        ...(isModal
                            ? {
                                  maxHeight: "55vh",
                                  // Mobile responsiveness for modal
                                  "@media (max-width: 600px)": {
                                      maxHeight: "55vh",
                                  },
                              }
                            : {}),
                    }}
                    className={isModal ? "custom-scrollbar" : ""}
                >
                    {/* Event-Action Mappings */}
                    {events?.length > 0 && actions?.length > 0 && (
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    // Mobile responsiveness for header
                                    ...(isModal
                                        ? {
                                              "@media (max-width: 600px)": {
                                                  flexDirection: "column",
                                                  alignItems: "flex-start",
                                                  gap: 1,
                                              },
                                          }
                                        : {}),
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                        // Mobile responsiveness for title
                                        ...(isModal
                                            ? {
                                                  "@media (max-width: 600px)": {
                                                      fontSize: "1.1rem",
                                                  },
                                              }
                                            : {}),
                                    }}
                                >
                                    Event-Action Mappings
                                </Typography>
                                <Chip
                                    label={`${fields.length} mapping${
                                        fields.length !== 1 ? "s" : ""
                                    }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => {}}
                                />
                            </Box>

                            {fields.map((row, rowIndex) => (
                                <Card
                                    key={row.id || rowIndex}
                                    ref={(el) => {
                                        if (row.id) {
                                            fieldRefs.current[row.id] = el;
                                        }
                                    }}
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        backgroundColor: "#fff",
                                        transition: "all 0.2s ease-in-out",
                                    }}
                                >
                                    <CardContent sx={{ p: 0 }}>
                                        {/* Header */}
                                        <Box
                                            sx={{
                                                p: 3,
                                                pb: row.isExpanded ? 2 : 3,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                // Mobile responsiveness for field header
                                                ...(isModal
                                                    ? {
                                                          "@media (max-width: 600px)":
                                                              {
                                                                  p: 2,
                                                                  pb: row.isExpanded
                                                                      ? 1
                                                                      : 2,
                                                                  flexDirection:
                                                                      "column",
                                                                  alignItems:
                                                                      "stretch",
                                                                  gap: 2,
                                                              },
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                gap={2}
                                                sx={{
                                                    flex: 1,
                                                    // Mobile responsiveness for form controls
                                                    ...(isModal
                                                        ? {
                                                              "@media (max-width: 600px)":
                                                                  {
                                                                      flexDirection:
                                                                          "column",
                                                                      spacing: 1,
                                                                      rowGap: 2,
                                                                  },
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                {/* Event Selection */}
                                                <FormControl
                                                    sx={{
                                                        minWidth: 200,
                                                        // Mobile responsiveness for form controls
                                                        ...(isModal
                                                            ? {
                                                                  "@media (max-width: 600px)":
                                                                      {
                                                                          minWidth:
                                                                              "100%",
                                                                      },
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    <Autocomplete
                                                        freeSolo
                                                        options={
                                                            row.event_name
                                                                .options
                                                        }
                                                        getOptionLabel={(
                                                            option,
                                                        ) => {
                                                            // If option is a string (custom input), return it
                                                            if (
                                                                typeof option ===
                                                                "string"
                                                            ) {
                                                                return option;
                                                            }
                                                            // If option is an object, return its label
                                                            return (
                                                                option.label ||
                                                                ""
                                                            );
                                                        }}
                                                        value={
                                                            row.event_name.options.find(
                                                                (opt) =>
                                                                    opt.value ===
                                                                    row
                                                                        .event_name
                                                                        .value,
                                                            ) ||
                                                            row.event_name
                                                                .value ||
                                                            null
                                                        }
                                                        onChange={(
                                                            event,
                                                            newValue,
                                                        ) => {
                                                            let valueToSet = "";

                                                            // If newValue is an object (selected from dropdown)
                                                            if (
                                                                newValue &&
                                                                typeof newValue ===
                                                                    "object"
                                                            ) {
                                                                valueToSet =
                                                                    newValue.value;
                                                            }
                                                            // If newValue is a string (custom input)
                                                            else if (
                                                                typeof newValue ===
                                                                "string"
                                                            ) {
                                                                valueToSet =
                                                                    newValue;
                                                            }

                                                            handleFieldChange({
                                                                rowIndex,
                                                                name: "event_name",
                                                                value: valueToSet,
                                                            });
                                                        }}
                                                        onInputChange={(
                                                            event,
                                                            newInputValue,
                                                        ) => {
                                                            // This handles the case when user types custom value
                                                            if (
                                                                event?.type ===
                                                                "change"
                                                            ) {
                                                                handleFieldChange(
                                                                    {
                                                                        rowIndex,
                                                                        name: "event_name",
                                                                        value: newInputValue,
                                                                    },
                                                                );
                                                            }
                                                        }}
                                                        renderInput={(
                                                            params,
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                data-cy="create-destination-sstask-event"
                                                                required
                                                                label="Event"
                                                                name="event_name"
                                                                size="small"
                                                                variant="outlined"
                                                                placeholder="Select or enter custom event"
                                                                error={
                                                                    hasAttemptedSave &&
                                                                    !!validationErrors
                                                                        .fields?.[
                                                                        rowIndex
                                                                    ]
                                                                        ?.event_name
                                                                }
                                                                helperText={
                                                                    hasAttemptedSave &&
                                                                    validationErrors
                                                                        .fields?.[
                                                                        rowIndex
                                                                    ]
                                                                        ?.event_name
                                                                }
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root":
                                                                        {
                                                                            transition:
                                                                                "all 0.2s ease-in-out",
                                                                            "&:hover":
                                                                                {
                                                                                    "& .MuiOutlinedInput-notchedOutline":
                                                                                        {
                                                                                            borderColor:
                                                                                                "primary.main",
                                                                                        },
                                                                                },
                                                                            "&.Mui-focused":
                                                                                {
                                                                                    "& .MuiOutlinedInput-notchedOutline":
                                                                                        {
                                                                                            borderColor:
                                                                                                "primary.main",
                                                                                            borderWidth: 2,
                                                                                        },
                                                                                },
                                                                        },
                                                                }}
                                                            />
                                                        )}
                                                        size="small"
                                                        sx={{
                                                            "& .MuiAutocomplete-popupIndicator":
                                                                {
                                                                    color: "primary.main",
                                                                },
                                                        }}
                                                    />
                                                </FormControl>

                                                {/* Action Selection */}
                                                <FormControl
                                                    style={{ marginLeft: 0 }}
                                                    sx={{
                                                        minWidth: 200,
                                                        // Mobile responsiveness for form controls
                                                        ...(isModal
                                                            ? {
                                                                  "@media (max-width: 600px)":
                                                                      {
                                                                          minWidth:
                                                                              "100%",
                                                                      },
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    <TextField
                                                        select
                                                        required
                                                        label="Action"
                                                        name="action_code"
                                                        inputProps={{
                                                            "data-cy":
                                                                "create-destination-sstask-action",
                                                        }}
                                                        value={
                                                            row.action_code
                                                                .value || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleFieldChange({
                                                                rowIndex,
                                                                name: "action_code",
                                                                value: e.target
                                                                    .value,
                                                            })
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                        error={
                                                            hasAttemptedSave &&
                                                            !!validationErrors
                                                                .fields?.[
                                                                rowIndex
                                                            ]?.action_code
                                                        }
                                                        helperText={
                                                            hasAttemptedSave &&
                                                            validationErrors
                                                                .fields?.[
                                                                rowIndex
                                                            ]?.action_code
                                                        }
                                                        sx={{
                                                            "& .MuiOutlinedInput-root":
                                                                {
                                                                    transition:
                                                                        "all 0.2s ease-in-out",
                                                                    "&:hover": {
                                                                        "& .MuiOutlinedInput-notchedOutline":
                                                                            {
                                                                                borderColor:
                                                                                    "primary.main",
                                                                            },
                                                                    },
                                                                    "&.Mui-focused":
                                                                        {
                                                                            "& .MuiOutlinedInput-notchedOutline":
                                                                                {
                                                                                    borderColor:
                                                                                        "primary.main",
                                                                                    borderWidth: 2,
                                                                                },
                                                                        },
                                                                },
                                                        }}
                                                    >
                                                        <MenuItem
                                                            value=""
                                                            disabled
                                                        >
                                                            Select an action
                                                        </MenuItem>
                                                        {row.action_code.options.map(
                                                            (option) => (
                                                                <MenuItem
                                                                    key={
                                                                        option.value
                                                                    }
                                                                    value={
                                                                        option.value
                                                                    }
                                                                >
                                                                    {
                                                                        option.label
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </TextField>
                                                </FormControl>
                                            </Stack>

                                            {/* Action Buttons */}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    // Mobile responsiveness for action buttons
                                                    ...(isModal
                                                        ? {
                                                              "@media (max-width: 600px)":
                                                                  {
                                                                      justifyContent:
                                                                          "center",
                                                                      width: "100%",
                                                                  },
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                {row.action_code.value &&
                                                    Object.keys(
                                                        row.parameters.value,
                                                    ).length > 0 && (
                                                        <Tooltip
                                                            title={
                                                                row.isExpanded
                                                                    ? "Hide parameters"
                                                                    : "Show parameters"
                                                            }
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    toggleExpansion(
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                color="primary"
                                                            >
                                                                {row.isExpanded ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                <Tooltip title="Remove mapping">
                                                    <IconButton
                                                        size="small"
                                                        disabled={
                                                            fields.length === 1
                                                        }
                                                        onClick={() =>
                                                            handleDeleteRow(
                                                                rowIndex,
                                                            )
                                                        }
                                                        color="error"
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Box>

                                        {/* Parameters Section */}
                                        {row.action_code.value &&
                                            Object.keys(row.parameters.value)
                                                .length > 0 && (
                                                <Collapse in={row.isExpanded}>
                                                    <Divider />
                                                    {renderParameters(
                                                        rowIndex,
                                                        row.parameters.value,
                                                    )}
                                                </Collapse>
                                            )}
                                    </CardContent>
                                </Card>
                            ))}

                            {/* Add New Mapping Button */}
                            <Box
                                sx={{
                                    display: "flex",
                                    pt: 2,
                                    // Mobile responsiveness for add button container
                                    ...(isModal
                                        ? {
                                              "@media (max-width: 600px)": {
                                                  pt: 1,
                                              },
                                          }
                                        : {}),
                                }}
                            >
                                <StandartButton
                                    variant={ButtonVariant.BLACK_OUTLINED}
                                    iconButton
                                    onClick={handleAddNewRow}
                                >
                                    <Stack direction="row" gap={1}>
                                        <AddCircle />
                                        <Box fontSize="14px" fontWeight={600}>
                                            ADD ROW
                                        </Box>
                                    </Stack>
                                </StandartButton>
                            </Box>
                        </Stack>
                    )}

                    {/* Loading State */}
                    {(!events?.length || !actions?.length) && (
                        <Card
                            elevation={0}
                            sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: 3,
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <CardContent sx={{ p: 4, textAlign: "center" }}>
                                <Stack spacing={2} alignItems="center">
                                    <ClipLoader color="#666" size={30} />
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                    >
                                        {!events?.length && !actions?.length
                                            ? "Loading events and actions..."
                                            : !events?.length
                                              ? "No events available"
                                              : "No actions available"}
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </>
        )
    );
};

export default SignalSightTaskDestination;
